/**
 * Platform helpers for web PWA vs future Capacitor shells.
 * Keep all Capacitor detection here so call/push/face code stays portable.
 */

export function getCapacitor() {
  if (typeof window === 'undefined') return null
  return window.Capacitor || null
}

export function isNativeApp() {
  const cap = getCapacitor()
  if (!cap) return false
  if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform()
  return Boolean(cap.getPlatform && cap.getPlatform() !== 'web')
}

export function getPlatformName() {
  const cap = getCapacitor()
  if (cap && typeof cap.getPlatform === 'function') return cap.getPlatform()
  return 'web'
}

export function isIOS() {
  if (getPlatformName() === 'ios') return true
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

export function isAndroid() {
  if (getPlatformName() === 'android') return true
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent)
}

/** Prefer native push on Capacitor; web-push on browser/PWA. */
export function prefersNativePush() {
  return isNativeApp()
}

const DEFAULT_PUBLIC_ORIGIN = 'https://trophytime.pages.dev'

/**
 * The public, shareable origin for links that LEAVE the device
 * (invite emails, deep links, Universal Links).
 *
 * Must never emit a Capacitor shell origin (capacitor://localhost) or a dev
 * localhost host into an external link. Order:
 *   1. Baked production URL (REACT_APP_PRODUCTION_URL)
 *   2. Native app with no env → hardcoded production default (never capacitor://)
 *   3. Real web origin (supports preview deploys + localhost dev)
 *   4. Hardcoded production default
 */
export function getPublicAppOrigin() {
  const fromEnv = process.env.REACT_APP_PRODUCTION_URL
  if (fromEnv) return fromEnv.replace(/\/$/, '')

  if (isNativeApp()) return DEFAULT_PUBLIC_ORIGIN

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }
  return DEFAULT_PUBLIC_ORIGIN
}

/** Build an absolute, shareable app URL from a path (e.g. `/join/abc`). */
export function buildPublicAppUrl(path = '/') {
  const base = getPublicAppOrigin()
  const suffix = String(path || '')
  return `${base}${suffix.startsWith('/') ? '' : '/'}${suffix}`
}
